import React, { useCallback, useEffect, useRef, useState } from "react";
import Cookies from "js-cookie";
import api from "../../chat/api";
import Shell from "./CopilotCardShell";

/* Proposal setup, in the conversation.
 *
 * Same questions and same storage as the /proposal setup page — `proposal-config`
 * — so a project set up here and one set up there are the same project. Only
 * where the questions get asked has changed.
 *
 * It is a CARD, not a form. The first version numbered five blocks and put a
 * hint under each: numbering five things implies five decisions, and explaining
 * every one of them makes a two-second choice look like paperwork. Every field
 * here already has a sensible default, so the honest shape is "press Generate,
 * or change one thing first" — five quiet label-and-control rows, all visible.
 *
 * Nothing is behind a disclosure. A "More options" toggle was tried and removed:
 * it hid two settings to save two rows, and hiding a setting is only a saving
 * for the people who never wanted it. Structure especially — a template is a
 * fixed section list, so choosing one changes the outline, every count
 * downstream and the document itself. That is not a decision to bury in a menu,
 * which is why both structure options sit on screen and the template picker
 * appears underneath only once it has something to pick between.
 *
 * Two things about the endpoint drive the save logic:
 *
 *   IT APPENDS lists. POSTing `usps` adds to what is stored rather than
 *   replacing it, so saving twice would leave the same differentiator in twice.
 *   Every save is therefore a DIFF, and deletions go through /remove/ with the
 *   id the server assigned.
 *
 *   IT IS PARTIAL. Only the keys sent change, which is what lets structure be
 *   saved on its own without disturbing tone or the differentiators.
 */

const TONES = [
  { api: "professional", label: "Professional & results-led" },
  { api: "formal", label: "Formal & compliance-led" },
  { api: "technical", label: "Innovative & visionary" },
];

/* How much prose each section runs to. "short" is the DEFAULT — it compresses the
 * writing without dropping any requirement coverage. "long" is what the generator
 * has always done, kept as an explicit choice for bids that want the fuller
 * treatment. Requirement coverage is unchanged at either setting. */
const LENGTHS = [
  { api: "short", label: "Concise" },
  { api: "long", label: "Detailed" },
];

const MAX_USPS = 6;

/* One row: label left, control right. */
const Row = ({ label, children }) => (
  <div className="flex items-center gap-3 px-4 py-2.5">
    <span className="w-[86px] flex-none text-[12.5px] text-[#6B6472]">{label}</span>
    <div className="min-w-0 flex-1">{children}</div>
  </div>
);

const FIELD =
  "w-full rounded-lg border border-[#18101C]/[0.10] bg-white px-2.5 py-[6px] text-[13px] text-[#18101C] outline-none transition-colors placeholder:text-[#77707E] focus:border-[#A04A97]/50 motion-reduce:transition-none";

const ProposalSetupCard = ({ message, onGenerate, carry, onCarry }) => {
  const { projectId, projectName } = message;
  // What the user already said in the message that opened this card.
  const prefill = message.prefill || {};
  const prefilled = Object.keys(prefill).length > 0;
  // Replaced by a newer setup card further down the transcript. Its values
  // are stale, so pressing Generate on it would save what the user just
  // changed away from.
  const superseded = !!message.superseded;

  const [tone, setTone] = useState("professional");
  const [length, setLength] = useState("short");
  const [usps, setUsps] = useState([]); // [{ id, value }] — id assigned by the server
  const [draftUsp, setDraftUsp] = useState("");
  const [instructions, setInstructions] = useState("");
  const [structure, setStructure] = useState("agentic");
  const [templateId, setTemplateId] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [coverLetter, setCoverLetter] = useState(false);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [started, setStarted] = useState(false);
  // Distinct from "none saved". A failed request that renders as an empty
  // list tells the user their templates do not exist, which is a lie.
  const [templatesFailed, setTemplatesFailed] = useState(false);

  // What the server already holds, so a save can send only what is new.
  const savedRef = useRef({ tone: null, instructions: "", uspValues: new Set() });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        // organization_id is REQUIRED here — without it the endpoint 400s, and
        // a swallowed 400 renders as "no templates saved", which is how a user
        // with a shelf full of templates gets told they have none.
        const orgId = Cookies.get("selected_org_id");
        const [cfg, tpl] = await Promise.all([
          api.get(`/api/v1/rfp/folders/${projectId}/proposal-config/`),
          orgId
            ? api
                .get(`/api/v1/rfp/proposal-templates/?organization_id=${encodeURIComponent(orgId)}`)
                .catch(() => "failed")
            : Promise.resolve("failed"),
        ]);
        if (!alive) return;

        const c = cfg?.data?.proposal_config || {};
        const carried = (carry || {}).usps;
        const items = (Array.isArray(carried) ? carried : c.usps || []).map((u) =>
          typeof u === "string"
            ? { id: null, value: u }
            : { id: u?.id ?? null, value: u?.value ?? "" }
        );
        /* Stored config first, then anything the user just said on top.
         *
         * That order matters: what they typed a second ago is a fresher
         * statement of intent than what the project was configured with last
         * week, and being shown the stale value would make them set it twice.
         * USPs MERGE rather than replace — the ones on the project are still
         * true; the ones they just named are additions. */
        const storedCover =
          typeof (carry || {}).cover_letter === "boolean"
            ? carry.cover_letter
            : (cfg?.data?.proposal_documents || []).some(
                (d) => d.id === "cover_letter" && d.enabled
              );

        /* Three layers, weakest first.
         *
         *   stored   what the project was configured with
         *   carry    what the user had on the PREVIOUS card but never saved —
         *            settings only reach the server on Generate, so without
         *            this a card reopened to change one field silently reset
         *            every other field to the stored value
         *   prefill  what they just said, which is the freshest of the three
         */
        const base = { ...c, ...(carry || {}) };

        setTone(prefill.tone || base.tone || "professional");
        setLength(prefill.length || base.length || "long");
        setInstructions(
          typeof prefill.instructions === "string"
            ? prefill.instructions
            : base.custom_instructions || ""
        );
        setStructure(prefill.structure || base.structure_source || "agentic");
        setTemplateId(prefill.template_id || base.template_id || null);
        setCoverLetter(
          typeof prefill.cover_letter === "boolean" ? prefill.cover_letter : storedCover
        );

        /* An explicit empty list means "clear them", so it cannot be treated as
         * "nothing to merge". Anything else merges: what is on the project is
         * still true, and what they just named is an addition. */
        if (Array.isArray(prefill.usps) && prefill.usps.length === 0) {
          setUsps([]);
        } else {
          const merged = [...items];
          const have = new Set(items.map((i) => i.value.toLowerCase()));
          for (const v of prefill.usps || []) {
            if (!have.has(v.toLowerCase()) && merged.length < MAX_USPS) {
              have.add(v.toLowerCase());
              merged.push({ id: null, value: v });
            }
          }
          setUsps(merged);
        }
        savedRef.current = {
          tone: c.tone || null,
          instructions: c.custom_instructions || "",
          length: c.length || "long",
          uspValues: new Set(items.map((i) => i.value.toLowerCase())),
        };

        if (tpl === "failed") {
          setTemplatesFailed(true);
          setTemplates([]);
        } else {
          const list = tpl?.data?.results || [];
          setTemplates(Array.isArray(list) ? list : []);
        }
      } catch {
        if (alive) setError("Couldn't load this project's proposal settings.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const addUsp = () => {
    const v = draftUsp.trim();
    if (!v || usps.length >= MAX_USPS) return;
    if (usps.some((u) => u.value.toLowerCase() === v.toLowerCase())) {
      setDraftUsp("");
      return;
    }
    setUsps((prev) => [...prev, { id: null, value: v }]);
    setDraftUsp("");
  };

  /* Removing is not only local state: anything the server has an id for must be
   * deleted there too, or the next read brings it back. */
  const removeUsp = async (item) => {
    setUsps((prev) => prev.filter((u) => u !== item));
    savedRef.current.uspValues.delete(item.value.toLowerCase());
    if (!item.id) return;
    try {
      await api.post(`/api/v1/rfp/folders/${projectId}/proposal-config/remove/`, { id: item.id });
    } catch {
      setError("Couldn't remove that USP.");
    }
  };

  const canStart =
    !loading && !saving && !started && !superseded && (structure !== "template" || !!templateId);

  const start = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      const known = savedRef.current;

      // Only what changed. `usps` appends server-side, so re-sending the whole
      // list would duplicate every differentiator already stored.
      const payload = {};
      if (tone !== known.tone) payload.tone = tone;
      if (length !== known.length) payload.length = length;
      if (instructions !== known.instructions) payload.custom_instructions = instructions;
      const fresh = usps.filter((u) => !known.uspValues.has(u.value.toLowerCase()));
      if (fresh.length) payload.usps = fresh.map((u) => u.value);
      payload.cover_letter = coverLetter;

      if (Object.keys(payload).length) {
        await api.post(`/api/v1/rfp/folders/${projectId}/proposal-config/`, payload);
      }

      // Structure goes separately, matching the setup page: the backend drops a
      // template_id unless structure_source says 'template'.
      await api.post(
        `/api/v1/rfp/folders/${projectId}/proposal-config/`,
        structure === "template"
          ? { structure_source: "template", template_id: templateId }
          : { structure_source: "agentic" }
      );

      setStarted(true);
      onGenerate?.({ projectId, projectName });
    } catch {
      setError("Couldn't save those settings. Nothing has been generated.");
    } finally {
      setSaving(false);
    }
  }, [tone, length, instructions, usps, coverLetter, structure, templateId, projectId, projectName, onGenerate]);

  /* Hand the current values to whoever opens the next card.
   * A retired card stops reporting — its values are the ones being replaced. */
  useEffect(() => {
    if (loading || superseded) return;
    onCarry?.({
      tone,
      length,
      custom_instructions: instructions,
      structure_source: structure,
      template_id: templateId,
      cover_letter: coverLetter,
      usps,
    });
  }, [loading, superseded, tone, length, instructions, structure, templateId, coverLetter, usps, onCarry]);

  const uspsLeft = MAX_USPS - usps.length;

  return (
    <Shell
      icon={
        <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
          <polyline points="14 3 14 8 19 8" />
          <path d="M9 13h6M9 16.5h4" />
        </svg>
      }
      title="Write the proposal"
      meta={
        projectName ? (
          <span className="block max-w-[150px] truncate text-[12px] text-[#6B6472]">{projectName}</span>
        ) : null
      }
      footer={
        started ? null : (
        <>
          <button
            type="button"
            onClick={start}
            disabled={!canStart}
            className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-br from-[#A64E9D] to-[#8E4187] px-3.5 py-2 text-[13.5px] font-semibold text-white ring-1 ring-inset ring-white/[0.16] shadow-[0_1px_2px_rgba(24,16,28,0.10),0_8px_18px_-10px_rgba(142,65,135,0.65)] transition-[transform,box-shadow,filter] duration-200 hover:-translate-y-[1px] hover:brightness-[1.04] active:translate-y-0 disabled:cursor-not-allowed disabled:opacity-55 disabled:hover:transform-none focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97] focus-visible:ring-offset-2 motion-reduce:transition-none"
          >
            {saving && (
              <span className="h-3.5 w-3.5 animate-spin rounded-full border-[2px] border-white/40 border-t-white motion-reduce:animate-none" />
            )}
            {superseded
              ? "Superseded"
              : started
              ? "Generating…"
              : saving
              ? "Saving…"
              : "Generate proposal"}
          </button>
          {/* The promise that makes the wait ahead tolerable. It appears here and
              again on the planning card, deliberately. */}
          <span className="text-[12px] text-[#6B6472]">
            You'll approve the outline first.
          </span>
        </>
        )
      }
    >
      {/* Once Generate is pressed this card is a record, not a control. Leaving
          it full height with a dead "Generating…" button while the next step
          renders below turns one action into two competing cards on screen. */}
      {started && !superseded && (
        <p className="flex items-center gap-2 px-4 py-2.5 text-[12.5px] text-[#6B6472]">
          <svg aria-hidden="true" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#1F7D38" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="flex-none">
            <polyline points="20 6 9 17 4 12" />
          </svg>
          Setup saved — {TONES.find((t) => t.api === tone)?.label.toLowerCase()}
          {usps.length ? `, ${usps.length} USP${usps.length === 1 ? "" : "s"}` : ""}
          {structure === "template" ? ", from a template" : ", built from the RFP"}
        </p>
      )}

      {superseded && (
        <p className="border-b border-[#18101C]/[0.05] bg-[#18101C]/[0.03] px-4 py-2 text-[12px] text-[#6B6472]">
          Replaced by a newer setup below.
        </p>
      )}

      {/* Says why the card is not empty. Without it, values the user never
          typed into this card look like settings that were already there. */}
      {!loading && !superseded && prefilled && (
        <p className="flex items-start gap-1.5 border-b border-[#18101C]/[0.05] bg-[#A04A97]/[0.04] px-4 py-2 text-[12px] leading-relaxed text-[#6B6472]">
          <svg aria-hidden="true" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#8B3F83" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mt-[1px] flex-none">
            <polyline points="20 6 9 17 4 12" />
          </svg>
          <span>
            Filled in from what you asked — change anything before generating.
          </span>
        </p>
      )}

      {started ? null : loading ? (
        <div className="space-y-2 px-4 py-3.5" aria-hidden="true">
          {[0, 1].map((i) => (
            <div key={i} className="h-8 animate-pulse rounded-lg bg-[#18101C]/[0.05]" />
          ))}
        </div>
      ) : (
        <>
          <div className="divide-y divide-[#18101C]/[0.05]">
            <Row label="Tone">
              <select value={tone} onChange={(e) => setTone(e.target.value)} aria-label="Tone" className={FIELD}>
                {TONES.map((t) => (
                  <option key={t.api} value={t.api}>
                    {t.label}
                  </option>
                ))}
              </select>
            </Row>

            <Row label="Length">
              {/* Buttons rather than a select: three short words fit on one row,
                  and a length choice is worth seeing without opening anything.
                  Same treatment as Structure below — the two settings that
                  change what gets written are both visible at rest. */}
              <div className="flex gap-1.5">
                {LENGTHS.map((o) => {
                  const on = length === o.api;
                  return (
                    <button
                      key={o.api}
                      type="button"
                      onClick={() => setLength(o.api)}
                      aria-pressed={on}
                      disabled={saving}
                      className={`flex-1 rounded-lg border px-2.5 py-[6px] text-[12.5px] font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-55 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97] motion-reduce:transition-none ${
                        on
                          ? "border-[#A04A97]/45 bg-[#A04A97]/[0.09] text-[#8B3F83]"
                          : "border-[#18101C]/[0.10] text-[#6B6472] hover:bg-[#18101C]/[0.03]"
                      }`}
                    >
                      {o.label}
                    </button>
                  );
                })}
              </div>
            </Row>

            <Row label="Structure">
              {/* Both options on screen, not inside a select.
                  This is the choice that decides what actually gets written —
                  a template is a fixed section list, so picking one changes the
                  outline, every count downstream and the document itself. A
                  decision that consequential should not be one the reader has
                  to open a menu to discover they had. */}
              <div className="flex gap-1.5">
                {[
                  { key: "agentic", label: "From the RFP", enabled: true },
                  {
                    key: "template",
                    label: templates.length
                      ? "Use a template"
                      : templatesFailed
                      ? "Templates unavailable"
                      : "No templates saved",
                    enabled: !!templates.length,
                  },
                ].map((o) => (
                  <button
                    key={o.key}
                    type="button"
                    onClick={() => o.enabled && setStructure(o.key)}
                    disabled={!o.enabled}
                    aria-pressed={structure === o.key}
                    className={`flex-1 rounded-lg border px-2.5 py-[6px] text-[12.5px] font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-55 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97] motion-reduce:transition-none ${
                      structure === o.key
                        ? "border-[#A04A97]/45 bg-[#A04A97]/[0.09] text-[#8B3F83]"
                        : "border-[#18101C]/[0.10] text-[#6B6472] hover:bg-[#18101C]/[0.03]"
                    }`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
              {/* Only asked once the answer matters. */}
              {structure === "template" && !!templates.length && (
                <select
                  value={templateId || ""}
                  onChange={(e) => setTemplateId(e.target.value || null)}
                  aria-label="Which template"
                  className={`${FIELD} mt-1.5`}
                >
                  <option value="">Choose a template…</option>
                  {templates.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              )}
            </Row>

            <Row label="USPs">
              {!!usps.length && (
                <ul className="mb-1.5 flex flex-wrap gap-1.5">
                  {usps.map((u, i) => (
                    <li
                      key={`${u.value}-${i}`}
                      className="inline-flex max-w-full items-center gap-1.5 rounded-full bg-[#A04A97]/[0.09] py-[3px] pl-2.5 pr-1.5 text-[12.5px] text-[#8B3F83]"
                    >
                      <span className="truncate">{u.value}</span>
                      <button
                        type="button"
                        onClick={() => removeUsp(u)}
                        aria-label={`Remove USP: ${u.value}`}
                        className="flex h-4 w-4 flex-none items-center justify-center rounded-full text-[#8B3F83]/70 transition-colors hover:bg-[#A04A97]/15 hover:text-[#8B3F83] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97] motion-reduce:transition-none"
                      >
                        <svg aria-hidden="true" width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
                          <line x1="18" y1="6" x2="6" y2="18" />
                          <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {uspsLeft > 0 && (
                <>
                  {/* The placeholder is the only place this can say "more than
                      one". A field labelled USPs that reads like a single
                      answer gets a single answer — and the plural label alone
                      does not tell anyone how to add the second. */}
                  <input
                    value={draftUsp}
                    onChange={(e) => setDraftUsp(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addUsp();
                      }
                    }}
                    onBlur={addUsp}
                    placeholder={
                      usps.length
                        ? "Add another — press Enter"
                        : "What wins this bid? Press Enter to add"
                    }
                    aria-label={usps.length ? "Add another USP" : "Add a USP"}
                    className={FIELD}
                  />
                  <p className="mt-1 text-[11.5px] text-[#77707E]">
                    {usps.length
                      ? `${usps.length} added · room for ${uspsLeft} more`
                      : `Optional. Add up to ${MAX_USPS} — each one is woven through every section.`}
                  </p>
                </>
              )}
              {uspsLeft <= 0 && (
                <p className="text-[11.5px] text-[#77707E]">
                  {MAX_USPS} added — that's the maximum.
                </p>
              )}
            </Row>

            <Row label="Notes">
      <textarea
        value={instructions}
        onChange={(e) => setInstructions(e.target.value)}
        rows={2}
        placeholder="We're the incumbent — reference the 2023 rollout by name."
        aria-label="Extra instructions"
        className={`${FIELD} resize-y leading-relaxed`}
      />
    </Row>
    <Row label="Cover letter">
      <button
        type="button"
        onClick={() => setCoverLetter((v) => !v)}
        aria-pressed={coverLetter}
        className={`inline-flex items-center gap-2 rounded-full border px-2.5 py-[4px] text-[12.5px] font-medium transition-colors motion-reduce:transition-none ${
          coverLetter
            ? "border-[#A04A97]/45 bg-[#A04A97]/[0.09] text-[#8B3F83]"
            : "border-[#18101C]/[0.10] text-[#6B6472] hover:bg-[#18101C]/[0.03]"
        }`}
      >
        <span
          className={`flex h-3.5 w-3.5 items-center justify-center rounded-[4px] border ${
            coverLetter ? "border-[#A04A97] bg-[#A04A97]" : "border-[#B4AEBA]"
          }`}
        >
          {coverLetter && (
            <svg aria-hidden="true" width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3.6" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          )}
        </span>
        {coverLetter ? "Included" : "Not included"}
      </button>
      {/* Deliberately no submission checklist here — it is generated
          at the end, where it can describe a document that exists. */}
    </Row>
          </div>

          {error && (
            <p role="alert" className="border-t border-[#18101C]/[0.06] px-4 py-2.5 text-[12.5px] text-[#B91C1C]">
              {error}
            </p>
          )}
        </>
      )}
    </Shell>
  );
};

export default ProposalSetupCard;
